import { useState } from 'react';

import { Group, MultiSelect, Stack, Text, Title } from '@mantine/core';
import { type UseFormReturnType, useForm } from '@mantine/form';
import type { ContextModalProps } from '@mantine/modals';
import { notifications } from '@mantine/notifications';

import { IconChevronRight, IconCopy } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../shared/components/UI/Button/Button';

import { isValidEmail } from '../../shared/utils/strings';
import {
  useSummarySheetShareMutation,
  useSummarySheetShareSendEmailsMutation,
} from './summarySheetShare.hook';

const BASE_URL = import.meta.env.VITE_LIVINGSTONE_BASE_URL;

type SummarySheetShareModalProps = {
  uuid: string | null;
  tenderId: number;
};

type FormValues = {
  emails: string[];
};

export function SummarySheetShareModal({
  context,
  id,
  innerProps,
}: ContextModalProps<SummarySheetShareModalProps>) {
  const { t } = useTranslation(['common', 'shared']);
  const [uuid, setUuid] = useState(innerProps.uuid);

  const summarySheetShareMutation = useSummarySheetShareMutation({
    onSuccess: ({ uuid }) => {
      setUuid(uuid);
    },
  });
  const generateSharedLink = () => {
    summarySheetShareMutation.mutate({
      tenderId: innerProps.tenderId,
    });
  };

  const summarySheetShareSendEmailsMutation =
    useSummarySheetShareSendEmailsMutation({
      onSuccess: () => {
        context.closeModal(id);
        notifications.show({
          message: 'Emails envoyés !',
          color: 'blue',
          autoClose: 1000,
          withCloseButton: false,
        });
      },
    });

  const form = useForm<FormValues>({
    initialValues: { emails: [] },
    validate: {
      emails: (value: string[]) => {
        if (value.length <= 0) {
          return t('fieldRequired');
        }
        if (value.some(e => !isValidEmail(e))) {
          return t('shared:modals.summarySheetShare.inviteByEmailError');
        }
        return null;
      },
    },
  });

  const onSubmit = async (values: FormValues) => {
    if (!form.validate().hasErrors) {
      summarySheetShareSendEmailsMutation.mutate({
        emails: values.emails,
        tenderId: innerProps.tenderId,
      });
    }
  };

  return (
    <Stack p="02">
      <form onSubmit={e => e.preventDefault()}>
        <Title order={4} pb="05">
          {t('shared:modals.summarySheetShare.title')}
        </Title>

        <Stack spacing="02">
          {uuid ? (
            <ShareByLink uuid={uuid} />
          ) : (
            <CreateShareLink onClick={() => generateSharedLink()} />
          )}

          <Text variant="sm" fw="400" c="gray.6">
            {t('or')}
          </Text>

          <Text variant="md">
            {t('shared:modals.summarySheetShare.inviteByEmailTitle')}
          </Text>
          <MultiSelectEmails form={form} />
        </Stack>

        <Group noWrap position="right" spacing="03" pt="05">
          <Button
            variant="subtle"
            onClick={() => {
              context.closeModal(id);
            }}
          >
            {t('cancel')}
          </Button>
          <Button
            size="md"
            onClick={form.onSubmit(onSubmit)}
            disabled={!form.isValid()}
          >
            {t('share')}
          </Button>
        </Group>
      </form>
    </Stack>
  );
}

type MultiSelectEmailsProps = {
  form: UseFormReturnType<FormValues>;
};

function MultiSelectEmails({ form }: MultiSelectEmailsProps) {
  const { t } = useTranslation(['common', 'shared']);
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
  const [currentInput, setCurrentInput] = useState('');

  const addEmail = (newEmail: string) => {
    const trimedEmail = newEmail.trim();
    form.setFieldValue('emails', [...form.values.emails, trimedEmail]);
    setSelectedEmails([...selectedEmails, trimedEmail]);
    return trimedEmail;
  };

  const onChangemultiSelectEmails = (emails: string[]) => {
    form.setFieldValue('emails', emails);
  };

  return (
    <MultiSelect
      placeholder={t(
        'shared:modals.summarySheetShare.inviteByEmailPlaceholder',
      )}
      radius="md"
      data={selectedEmails}
      value={form.values.emails}
      onChange={onChangemultiSelectEmails}
      searchable
      creatable
      selectOnBlur
      onCreate={query => addEmail(query)}
      getCreateLabel={query => `+ ${t('add')} ${query}`}
      searchValue={currentInput}
      onSearchChange={setCurrentInput}
      onKeyDown={e => {
        if (e.key === 'Enter') {
          e.preventDefault();
          addEmail(currentInput);
          setCurrentInput('');
        }
      }}
    />
  );
}

type CreateShareLinkProps = {
  onClick: () => void;
};

function CreateShareLink({ onClick }: CreateShareLinkProps) {
  const { t } = useTranslation('shared');

  return (
    <Button w="100%" variant="outline" onClick={onClick}>
      {t('modals.summarySheetShare.generateLink')}
    </Button>
  );
}

type ShareByLinkProps = {
  uuid: string | null;
};

function ShareByLink({ uuid }: ShareByLinkProps) {
  const { t } = useTranslation('shared');
  const url = `${BASE_URL}/share/summary-sheet/${uuid}`;

  return (
    <Group spacing="01" noWrap>
      <Button
        w="100%"
        variant="outline"
        onClick={() => {
          copyToClipboard(url);
        }}
        leftIcon={<IconCopy />}
      >
        {t('modals.summarySheetShare.copyButton')}
      </Button>
      <Button
        variant="subtle"
        onClick={() => {
          window.open(url, '_blank');
        }}
        rightIcon={<IconChevronRight />}
      >
        {t('modals.summarySheetShare.openButton')}
      </Button>
    </Group>
  );
}

async function copyToClipboard(text: string) {
  try {
    await navigator.clipboard.writeText(text);
    notifications.show({
      message: 'Lien copié !',
      color: 'blue',
      autoClose: 1000,
      withCloseButton: false,
    });
  } catch (err) {
    notifications.show({
      title: 'Erreur',
      message: 'Impossible de copier le lien',
      color: 'red',
      autoClose: 2000,
      withCloseButton: false,
    });
  }
}
